import React from "react";
import { compareDateValues } from "../../../utils/dateUtils";

const riskValues = ["HIGH", "MEDIUM", "LOW"];

const isValidRisk = (v) => {
  const val = String(v || "").trim().toUpperCase();
  return val === "HIGH" || val === "MEDIUM" || val === "LOW";
};

const toDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const formatSupplierConfirmation = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  value === "-" ||
  value === "—" ||
  Number.isNaN(value)
    ? "N/A"
    : value;

const UpdatedByRenderer = (params) => {
  const entries = Array.isArray(params?.value) ? params.value.slice(0, 2) : [];
  if (!entries.length) {
    return React.createElement("span", null, "N/A");
  }

  return React.createElement(
    "div",
    { style: { display: "grid", gap: 4, whiteSpace: "normal", lineHeight: 1.2, paddingTop: 4, paddingBottom: 4 } },
    entries.map((item, index) => {
      const label = item?.name || item?.userName || item?.email || "-";
      const ts = toDisplayDateTime(item?.updatedAt || item?.timestamp || item?.time);
      return React.createElement(
        "div",
        { key: `${label}-${index}`, style: { fontSize: 11 } },
        `${label} (${ts})`
      );
    })
  );
};

const UserDefinedRiskCellRenderer = (params) => {
  const row = params?.data || {};
  const context = params?.context || {};
  const rowKey = context.getRiskRowKey?.(row) || `${row?.plant || ""}::${row?.material || ""}`;

  // Severity drives initial value and enable/disable state
  const severityValue = String(row?.severity || "").trim().toUpperCase();
  const severityIsValid = isValidRisk(severityValue);

  // Use saved userDefinedRiskStatus if it's a valid risk value;
  // otherwise pre-populate the dropdown with the current Risk Status value.
  // If Risk Status is N/A, always show "Select" regardless of saved value.
  const savedUserDefined = String(row?.userDefinedRiskStatus || "").trim().toUpperCase();
  const selectedValue = severityIsValid
    ? isValidRisk(savedUserDefined)
      ? savedUserDefined
      : severityValue
    : "";

  const isSaving = Boolean(context.isRiskRowSaving?.(rowKey));
  const canEditUserDefinedRisk = context.canEditUserDefinedRisk !== false;
  // Disable when Risk Status is N/A / not a recognised risk level
  const isControlDisabled = !severityIsValid || isSaving || !canEditUserDefinedRisk;

  return React.createElement(
    "div",
    { style: { display: "flex", alignItems: "center", gap: 8, height: "100%" } },
    [
      React.createElement(
        "select",
        {
          key: "risk-select",
          className: "user-defined-risk-select",
          value: selectedValue,
          disabled: isControlDisabled,
          onChange: (event) => {
            const nextValue = String(event?.target?.value || "").trim().toUpperCase();
            if (params?.node?.setDataValue) {
              params.node.setDataValue("userDefinedRiskStatus", nextValue);
            }
            context.onUserDefinedRiskChange?.(row, nextValue);
          },
          style: {
            minWidth: 100,
            height: 28,
            border: "1px solid #cbd5e1",
            borderRadius: 4,
            padding: "0 4px",
            fontSize: 12,
            backgroundColor: isControlDisabled ? "#f1f5f9" : "#ffffff",
            outline: "none",
            boxShadow: "none",
            cursor: isControlDisabled ? "not-allowed" : "pointer",
            margin: 0,
          },
        },
        [
          React.createElement("option", { key: "placeholder", value: "" }, "Select"),
          ...riskValues.map((riskValue) =>
            React.createElement("option", { key: riskValue, value: riskValue }, riskValue)
          ),
        ]
      ),
      React.createElement(
        "button",
        {
          key: "risk-save",
          type: "button",
          disabled: isControlDisabled || !selectedValue,
          onClick: () => context.onUserDefinedRiskSave?.(row, selectedValue),
          style: {
            height: 28,
            border: "1px solid #1976d2",
            borderRadius: 4,
            backgroundColor: isControlDisabled ? "#94a3b8" : "#1976d2",
            color: "#ffffff",
            padding: "0 10px",
            fontSize: 12,
            cursor: isControlDisabled ? "not-allowed" : "pointer",
            margin: 0,
          },
        },
        isSaving ? "Saving..." : "Save"
      ),
    ]
  );
};

export const ARDashboardColumns = () => {
  return [
    { headerName: "Plant Code", field: "plant", filter: true, width: 180 },
    { headerName: "Plant Description", field: "plant_name", filter: true, width: 200 },
    { headerName: "TCCC Material Number", field: "material", filter: true, width: 200, cellClass: "clickable-cell" },
    { headerName: "Description", field: "descriptions", filter: true, width: 200 },
    { headerName: "Unit", field: "unit", filter: true, width: 130 },
   
    {
      headerName: "Extraction Date",
      field: "extractionDate",
      filter: true,
      width: 150,
      comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      filterParams: {
        comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      },
    },
    {
      headerName: "Published Date",
      field: "publicationDate",
      filter: true,
      width: 150,
      comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      filterParams: {
        comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      },
    },
    { headerName: "Total Forecast", field: "sumofforecast", filter: true, width: 180 },
    {
      headerName: "Confirm Date",
      field: "creationDate",
      filter: true,
      width: 140,
      comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      filterParams: {
        comparator: (valueA, valueB) => compareDateValues(valueA, valueB),
      },
    },
    {
      headerName: "Total Supplier Confirmation",
      field: "sumconfirmquantity",
      filter: true,
      width: 220,
      valueFormatter: (params) => formatSupplierConfirmation(params.value),
    },
    { headerName: "Country", field: "country", filter: true, width: 150 },
    { headerName: "Region", field: "region", filter: true, width: 150 },
    { headerName: "Risk Status", field: "severity", filter: true, width: 150 },
    {
      headerName: "User Defined (RiskStatus)",
      field: "userDefinedRiskStatus",
      filter: true,
      width: 240,
      cellRenderer: UserDefinedRiskCellRenderer,
      cellClass: "no-cell-border-on-select",
      suppressKeyboardEvent: () => true,
    },
    {
      headerName: "Updated By",
      field: "updatedBy",
      filter: false,
      width: 270,
      autoHeight: true,
      cellRenderer: UpdatedByRenderer,
    },
    { headerName: "Variance(Confirmation - Forecast)", field: "variance", filter: true, width: 220 },
    { headerName: "Variance %", field: "variancePct", filter: true, width: 160 },
    { headerName: "Material Group Description", field: "materialGroupDesc", filter: true, width: 160 },
    { headerName: "Purchase Group", field: "purchasingGroup", filter: true, width: 160 },
    { headerName: "External Material Group", field: "scode", filter: true, width: 150 },
    { headerName: "MRP Controller", field: "mrpController", filter: true, width: 160 },
    { headerName: "GMP", field: "gmp", filter: true, width: 140 },
  ];
};